import { useState } from "react";
import LoadingState from "../components/basic/loading-state";
import { TablePagination } from "../components/basic/table";
import { resolveMediaUrl } from "../helpers/media-url";
import { igProfileUrl, ProfileAvatar, sessionTone } from "./shared";
import type { AccountRow, BotConfig, ProcessInfo } from "../params/types";

const PAGE_SIZE = 10;

function StatCount({ total, added }: { total: number; added: number }) {
  const x = Math.max(0, Math.round(Number(total) || 0));
  const y = Math.max(0, Math.round(Number(added) || 0));
  if (y <= 0) return <span className="ib-progress-table__stat">{x}</span>;
  return (
    <span className="ib-progress-table__stat">
      {x}
      {" / "}
      <span className="ib-progress-table__plus">+{y}</span>
    </span>
  );
}

const PlayIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const StopIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="6" width="12" height="12" rx="1.5" />
  </svg>
);

const PauseIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

function ageMs(iso?: string | null) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Date.now() - t);
}

function tickFreshness(iso: string | null | undefined, expectedMs: number) {
  const age = ageMs(iso);
  if (age == null || !expectedMs) return 0;
  return Math.max(0, Math.min(1, 1 - age / expectedMs));
}

/**
 * Progress 0–100 from live watcher tick freshness.
 * Always 0 when the account process is not running.
 */
function progressPct(account: AccountRow, pollIntervalMs: number = 50_000) {
  if (!account.process_running) return 0;

  const poll = Math.max(15_000, Number(pollIntervalMs) || 50_000);
  const session = String(account.session_status || "").toLowerCase();
  const watcher = String(account.watcher_status || "").toLowerCase();

  if (session === "banned") return 0;
  if (session === "needs_login" || session === "challenge") return 10;
  if (!watcher || watcher === "stopped" || watcher === "starting") return 20;
  if (watcher === "errored") return 15;

  const inboxAge = ageMs(account.last_inbox_tick_at);
  // Tabs are up but the inbox loop hasn't completed a tick yet.
  if (watcher === "watching" && inboxAge == null) return 35;

  const inboxExpected = poll * 2;
  const followersExpected = Math.max(poll * 2.2, 75_000) * 2;
  const requestsExpected = Math.max(poll * 2.8, 90_000) * 2;
  const tabScore =
    0.5 * tickFreshness(account.last_inbox_tick_at, inboxExpected) +
    0.25 * tickFreshness(account.last_followers_tick_at, followersExpected) +
    0.25 * tickFreshness(account.last_requests_tick_at, requestsExpected);

  if (watcher === "degraded") {
    return Math.round(35 + 40 * tabScore);
  }
  return Math.round(55 + 45 * tabScore);
}

function rotationLabel(proc?: ProcessInfo | null): string | null {
  if (!proc?.rotation_active) return null;
  const index = proc.rotation_batch_index;
  const count = proc.rotation_batch_count;
  const group =
    index && count ? `group ${index} of ${count}` : "group running";
  const until = formatRotationUntil(proc.rotation_next_at);
  return until ? `Rotation · ${group} · until ${until}` : `Rotation · ${group}`;
}

function formatRotationUntil(iso?: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return "";
  const time = at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (at.toDateString() === new Date().toDateString()) return time;
  const day = at.toLocaleDateString([], { month: "short", day: "numeric" });
  return `${day} ${time}`;
}

/** Action line under the bar — prefer live progress_detail from the bot. */
function progressAction(account: AccountRow) {
  const detail = String(account.progress_detail || "").trim();
  // A force-started disabled/banned account is still running — show its detail.
  if (account.process_running && detail) return detail;

  if (!account.process_running || account.enabled === false) {
    return "Paused";
  }

  const session = String(account.session_status || "").toLowerCase();
  const watcher = String(account.watcher_status || "").toLowerCase();
  const poll = 50_000;
  const inboxAge = ageMs(account.last_inbox_tick_at);

  if (session === "banned") return "Banned";
  if (session === "challenge") return "Waiting for verification…";
  if (session === "needs_login") return "Logging in…";
  if (account.ai_paused) return "AI paused";
  if (watcher === "errored") return "Needs attention — Chrome open";
  if (!watcher || watcher === "stopped" || watcher === "starting") {
    return "Starting…";
  }
  if (watcher === "degraded") return "Recovering tabs…";
  if (watcher === "watching") {
    if (inboxAge == null) return "Starting…";
    if (inboxAge > poll * 3) return "Waiting for inbox…";
    return "Watching inbox…";
  }
  return watcher.replace(/_/g, " ");
}

interface ProgressProps {
  accounts?: AccountRow[];
  proc?: ProcessInfo | null;
  processRunning?: boolean;
  busy?: boolean;
  loading?: boolean;
  config?: BotConfig | null;
  onToggleProcess?: () => void;
  onPauseAll?: (pause: boolean) => void | Promise<void>;
  onStartAccount?: (username: string) => void | Promise<void>;
  onStopAccount?: (username: string) => void | Promise<void>;
}

export default function Progress({
  accounts = [],
  proc,
  processRunning,
  busy,
  loading,
  onToggleProcess,
  onPauseAll,
  onStartAccount,
  onStopAccount,
}: ProgressProps) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const stopping = proc?.status === "STOPPING";
  const anyPaused = accounts.some((a) => a.paused);

  const total = accounts.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const clampedPage = Math.min(page, Math.max(0, pageCount - 1));
  const pagedAccounts = accounts.slice(
    clampedPage * pageSize,
    clampedPage * pageSize + pageSize,
  );

  const handleAccountToggle = async (account: AccountRow) => {
    const username = account.username;
    if (!username || rowBusy) return;
    // Start waits out Start all. Stop stays available the whole time an account is up.
    if (!account.process_running && (busy || stopping)) return;
    setRowBusy(username);
    try {
      if (account.process_running) {
        await onStopAccount?.(username);
      } else {
        await onStartAccount?.(username);
      }
    } finally {
      setRowBusy(null);
    }
  };

  const rotationText = rotationLabel(proc);

  return (
    <div className="ib-progress-view">
      <div className="ib-progress-toolbar">
        <div className="ib-accounts-count">
          {accounts.length} account{accounts.length !== 1 ? "s" : ""}
          {typeof proc?.running_count === "number"
            ? ` · ${proc.running_count} running`
            : ""}
          {rotationText ? (
            <span
              className="ib-progress-rotation"
              title={(proc?.rotation_wave || []).join(", ")}
            >
              {rotationText}
            </span>
          ) : null}
        </div>
        <div className="ib-progress-actions">
          <button
            type="button"
            className={`jf-btn jf-btn-sm ib-progress-toggle ${
              anyPaused ? "jf-btn-primary" : "jf-btn-secondary"
            }`}
            disabled={
              busy || loading || stopping || (!anyPaused && !processRunning)
            }
            title={
              anyPaused
                ? "Resume activity for all paused bots (browser stays open)"
                : "Pause all bot activity without closing the browser, so you can browse the accounts"
            }
            onClick={() => onPauseAll?.(!anyPaused)}
          >
            {anyPaused ? <PlayIcon /> : <PauseIcon />}
            {anyPaused ? "Resume all" : "Pause all"}
          </button>
          <button
            type="button"
            className={`jf-btn jf-btn-sm ib-progress-toggle ${
              processRunning ? "jf-btn-danger" : "jf-btn-primary"
            }`}
            disabled={
              busy ||
              loading ||
              stopping ||
              (!processRunning && !accounts.length)
            }
            onClick={onToggleProcess}
          >
            {processRunning ? <StopIcon /> : <PlayIcon />}
            {stopping ? "Stopping…" : processRunning ? "Stop all" : "Start all"}
          </button>
        </div>
      </div>

      <div className="ib-progress-table">
        <div className="ib-progress-table__header">
          <span>Account</span>
          <span>Current Progress</span>
          <span className="ib-progress-table__head-center">Logins</span>
          <span className="ib-progress-table__head-center">Followers</span>
          <span className="ib-progress-table__head-center">Messages</span>
          <span className="ib-progress-table__head-center">Run</span>
        </div>

        {loading && accounts.length === 0 && (
          <div className="ib-accounts-table__empty">
            <LoadingState label="Loading accounts…" />
          </div>
        )}

        {!loading && accounts.length === 0 && (
          <div className="ib-accounts-table__empty">
            <div className="ib-accounts-table__empty-title">
              No accounts yet
            </div>
            <div className="ib-accounts-table__empty-text">
              Add accounts from the Accounts tab to see progress here.
            </div>
          </div>
        )}

        {pagedAccounts.map((a) => {
          const pollMs =
            a.ai?.poll_interval_max_ms ||
            a.ai?.poll_interval_min_ms ||
            50_000;
          const pct = progressPct(a, pollMs);
          const fullName = `${a.first_name || ""} ${a.last_name || ""}`.trim();
          const igHandle = a.ig_username
            ? `@${String(a.ig_username).replace(/^@/, "")}`
            : "";
          const profileUrl = igProfileUrl(a);
          const followers =
            a.ig_follower_count != null
              ? a.ig_follower_count
              : a.known_followers_count || 0;
          const followersNew = a.followers_new_today ?? 0;
          const running = Boolean(a.process_running);
          const inactive =
            a.enabled === false ||
            String(a.session_status || "").toLowerCase() === "banned";
          const rowBusyNow = rowBusy === a.username;
          return (
            <div key={a.username} className="ib-progress-table__row">
              <div className="ib-progress-table__cell ib-progress-table__cell--account">
                <ProfileAvatar
                  url={profileUrl}
                  text={a.ig_username || a.username}
                  image={resolveMediaUrl(a.avatar_url) || null}
                />
                <div className="ib-progress-table__account-info">
                  {profileUrl ? (
                    <a
                      className="ib-progress-table__username"
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={profileUrl}
                    >
                      {igHandle || a.username}
                    </a>
                  ) : (
                    <span className="ib-progress-table__username">
                      {igHandle || a.username}
                    </span>
                  )}
                  <span className="ib-progress-table__fullname">
                    {fullName || (igHandle ? a.username : "—")}
                  </span>
                </div>
              </div>
              <div className="ib-progress-table__cell ib-progress-table__cell--progress">
                <div className="ib-progress-bar">
                  <div
                    className={`ib-progress-bar__fill ib-progress-bar__fill--${sessionTone(a.session_status)}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="ib-progress-table__action">
                  {progressAction(a)}
                </span>
              </div>
              <div className="ib-progress-table__cell ib-progress-table__cell--center">
                <StatCount
                  total={a.login_count_total ?? 0}
                  added={a.login_count_today ?? 0}
                />
              </div>
              <div className="ib-progress-table__cell ib-progress-table__cell--center">
                <StatCount total={followers} added={followersNew} />
              </div>
              <div className="ib-progress-table__cell ib-progress-table__cell--center">
                <StatCount
                  total={a.messages_total ?? 0}
                  added={a.sends_last_day ?? 0}
                />
              </div>
              <div className="ib-progress-table__cell ib-progress-table__cell--center">
                <button
                  type="button"
                  className={`jf-btn jf-btn-sm ib-progress-row-toggle ${
                    running ? "jf-btn-danger" : "jf-btn-primary"
                  }`}
                  disabled={
                    rowBusyNow ||
                    !a.username ||
                    (!running && (busy || loading || stopping))
                  }
                  title={
                    running
                      ? "Stop this account process"
                      : inactive
                        ? "Start this account anyway to check if the ban lifted. Start all still skips it."
                        : "Start dedicated process for this account"
                  }
                  onClick={() => handleAccountToggle(a)}
                >
                  {rowBusyNow ? (
                    "…"
                  ) : running ? (
                    <>
                      <StopIcon size={14} />
                      Stop
                    </>
                  ) : (
                    <>
                      <PlayIcon size={14} />
                      Start
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <TablePagination
          page={clampedPage}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          onGotoPage={setPage}
          onPreviousPage={() => setPage((p) => Math.max(0, p - 1))}
          onNextPage={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          onSetPageSize={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}
    </div>
  );
}
